import {
  type NilHundredResponse,
  type NilHundredResponseNil100FilterResponseRelatedPagedData,
  type NilRankingResponse,
  type NilRankingResponseRelatedNilRankingResponseRelatedPagedData,
} from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { useSite } from '@on3/ui-lib/index';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { Nil100ModuleItems } from 'components/Nil/Nil100ModuleItems';
import { Nil100WidgetItems } from 'components/Nil/Nil100WidgetItems';
import { NilSportWidgetItem } from 'components/Nil/NilSportWidgetItem';
import { Nil100 } from 'components/Svg/Nil100';
import { Tabs } from 'components/UI/Tabs';
import useAthlete from 'contexts/AthleteProvider';
import { useMemo } from 'react';
import { createTitle } from 'utils/meta';
import { AthleteType } from 'utils/player';

import styles from './NilTopFive.module.scss';

interface NilTopFiveProps {
  nil100?: NilHundredResponseNil100FilterResponseRelatedPagedData | null;
  nilSport?: NilRankingResponseRelatedNilRankingResponseRelatedPagedData | null;
  nilTeam?: NilHundredResponseNil100FilterResponseRelatedPagedData | null;
}

const getTopFive = (
  nilHundred: NilHundredResponse[],
  nilRanking: NilRankingResponse[],
  nilTeam: NilHundredResponse[],
) => {
  return [
    nilHundred.slice(0, 5),
    nilRanking.slice(0, 5),
    nilTeam.slice(0, 5),
  ] as const;
};

export const NilTopFive = ({ nil100, nilSport, nilTeam }: NilTopFiveProps) => {
  const { athlete, athleteType } = useAthlete();

  const { formatHref } = useDynamicRoute();
  const { currentSite } = useSite();
  const primaryColor = currentSite?.organization?.primaryColor || '#fe3b1f';

  const [nil100List, nilSportList, nilTeamList] = getTopFive(
    nil100?.list || [],
    nilSport?.list || [],
    nilTeam?.list || [],
  );

  const nilTeamOrg = nilTeam?.relatedModel?.organization;

  const tabs = useMemo(() => {
    const t = [];

    if (nil100List?.length) {
      t.push({
        label: 'National NIL 100',
        key: 'national',
      });
    }

    if (nilSportList?.length) {
      t.push({
        label: createTitle(athlete.defaultSport?.name || 'Sport', 'NIL 100'),
        key: 'sport',
      });
    }

    if (nilTeamList?.length && nilTeamOrg) {
      t.push({
        label: createTitle(nilTeamOrg.knownAs || nilTeamOrg.name, 'NIL 100'),
        key: 'team',
      });
    }

    return t;
  }, [
    athlete.defaultSport?.name,
    nil100List,
    nilSportList,
    nilTeamList,
    nilTeamOrg,
  ]);

  return (
    <section className={styles.block}>
      <header>
        <h2 aria-hidden={true} hidden>
          NIL 100
        </h2>
        <div
          className={styles.logoContainer}
          style={{ borderBottom: `2px solid ${primaryColor}` }}
        >
          <Nil100 className={styles.logo} fill={primaryColor} />
        </div>
      </header>

      <Tabs tabs={tabs}>
        {(active) => (
          <>
            <ol
              aria-hidden={active !== 'national'}
              className={styles.list}
              id="panel-national"
            >
              {nil100List.map((item) => (
                <Nil100ModuleItems {...item} key={item.person?.key} />
              ))}
            </ol>
            <ol
              aria-hidden={active !== 'sport'}
              className={styles.list}
              id="panel-sport"
            >
              {nilSportList.map((item, i: number) => (
                <NilSportWidgetItem
                  {...item}
                  indexRank={i + 1}
                  key={item.person?.key}
                />
              ))}
            </ol>
            <ol
              aria-hidden={active !== 'team'}
              className={styles.list}
              id="panel-team"
            >
              {nilTeamList.map((item, i: number) => (
                <Nil100WidgetItems
                  {...item}
                  indexRank={i + 1}
                  key={item.person?.key}
                />
              ))}
            </ol>
            <footer className={styles.links}>
              <Link
                href={
                  active === 'national'
                    ? '/nil/rankings/player/nil-valuations/'
                    : formatHref({
                        path: '/nil/rankings/player/[type]/[sport]/',
                        params: {
                          type: [AthleteType.Base, AthleteType.Star].includes(
                            athleteType,
                          )
                            ? 'high-school'
                            : 'college',
                          sport: slugify(athlete.defaultSport?.name),
                        },
                        query: {
                          'team-key':
                            active === 'team'
                              ? String(nilTeamOrg?.key)
                              : undefined,
                        },
                      })
                }
                useSiteColor
              >
                Complete List
              </Link>
              <Link
                href="/nil/news/about-on3-nil-valuation-per-post-value/"
                useSiteColor
              >
                About
              </Link>
            </footer>
          </>
        )}
      </Tabs>
    </section>
  );
};
